import React from "react";
import styled from "styled-components";
import { Grid, Card, Typography, Button, Statistic } from "@arco-design/web-react";
import { IconUserAdd, IconNotification, IconUserGroup } from "@arco-design/web-react/icon";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { FetchApi } from "../Api/AuthenticatedApi";
import { drawerWidth } from "../AppBarAndDrawer/AppBarAndDrawer";
import GettingStartedCard from "./GettingStartedCard";
import CompassRose from "./CompassRose";

const Row = Grid.Row;
const Col = Grid.Col;
const { Title, Text, Paragraph } = Typography;

// Offset the page past the fixed desktop sidebar so it isn't clipped underneath.
// Mirrors the offset that the shared Content component applies to other pages;
// Home keeps its own full-bleed layout instead of using Content's Container.
const Shell = styled.div`
  @media (min-width: 600px) {
    margin-left: ${drawerWidth}px;
  }
`;

// Chart-room hero band. Theme-aware: a warm chart-paper panel with navy ink in
// light mode, the original harbor-navy slab in dark mode (see brand.css).
const Hero = styled.section`
  position: relative;
  overflow: hidden;
  background: var(--ca-hero-bg);
  color: var(--ca-hero-title);
  border-bottom: 1px solid var(--ca-hero-border);
`;

const HeroInner = styled.div`
  width: 100%;
  max-width: 1040px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 56px 24px 60px;
  display: grid;
  grid-template-columns: 1.25fr 0.75fr;
  gap: 32px;
  align-items: center;
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    padding: 40px 20px 44px;
    text-align: center;
  }
`;

const Eyebrow = styled.div`
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 12px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ca-hero-accent);
  display: flex;
  align-items: center;
  gap: 10px;
  &::before {
    content: "";
    width: 24px;
    height: 1px;
    background: var(--ca-hero-accent);
    opacity: 0.7;
  }
  @media (max-width: 720px) {
    justify-content: center;
  }
`;

const HeroTitle = styled.h1`
  font-family: var(--ca-serif);
  font-weight: 600;
  font-size: clamp(30px, 5vw, 52px);
  line-height: 1.05;
  letter-spacing: -0.02em;
  margin: 16px 0 0;
  color: var(--ca-hero-title);
  em {
    font-style: italic;
    color: var(--ca-hero-accent);
  }
`;

const HeroLede = styled.p`
  margin: 16px 0 0;
  max-width: 42ch;
  color: var(--ca-hero-lede);
  font-size: 16px;
  line-height: 1.6;
  @media (max-width: 720px) {
    margin-inline: auto;
  }
`;

const CompassWrap = styled.div`
  width: 100%;
  max-width: 300px;
  aspect-ratio: 1;
  margin: 0 auto;
  @media (max-width: 720px) {
    max-width: 200px;
    margin-top: 8px;
  }
`;

const CardGrid = styled.div`
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 48px 16px 64px;
`;

const Footer = styled.footer`
  padding: 32px;
  text-align: center;
`;

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: rgb(var(--arcoblue-6));
  background: rgb(var(--arcoblue-1));
  margin-bottom: 12px;
`;

const QUICK_ACTIONS = [
  {
    name: "Signups",
    description: "Build opt-in campaigns with landing forms to grow your list.",
    icon: <IconUserAdd />,
    path: "/signups",
  },
  {
    name: "Broadcasts",
    description: "Send one-off email broadcasts to your contacts.",
    icon: <IconNotification />,
    path: "/broadcasts",
  },
  {
    name: "Contacts",
    description: "Manage your contacts and segment them into lists.",
    icon: <IconUserGroup />,
    path: "/contacts",
  },
];

// Tracks the active Arco theme so the canvas compass can be repainted with
// theme-appropriate colors (canvas can't read CSS vars). Mirrors the attribute
// AppBarAndDrawer toggles on <body>, and observes live changes to it.
function useIsDark() {
  const [dark, setDark] = React.useState(
    () => typeof document !== "undefined" && document.body.getAttribute("arco-theme") === "dark"
  );
  React.useEffect(() => {
    const obs = new MutationObserver(() =>
      setDark(document.body.getAttribute("arco-theme") === "dark")
    );
    obs.observe(document.body, { attributes: true, attributeFilter: ["arco-theme"] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

// On-light navy ink vs. the original on-navy white, matching brand.css's
// --ca-hero-compass-* tokens.
const COMPASS_LIGHT = {
  accent: "#b0762e",
  line: "rgba(14,47,65,0.12)",
  muted: "rgba(14,47,65,0.32)",
};
const COMPASS_DARK = {
  accent: "#d9a560",
  line: "rgba(255,255,255,0.14)",
  muted: "rgba(255,255,255,0.34)",
};

export function Home() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const isDark = useIsDark();
  const compass = isDark ? COMPASS_DARK : COMPASS_LIGHT;
  const [metrics, setMetrics] = React.useState({ campaigns: null, broadcasts: null, contacts: null });

  React.useEffect(() => {
    // The list endpoints return total_count; ask for a single row just to read it.
    const load = (url, key) =>
      FetchApi(
        getAccessTokenSilently,
        url + "?count=1&page=0",
        (data) => setMetrics((m) => ({ ...m, [key]: (data && data.total_count) || 0 })),
        () => {},
        () => {}
      );
    load("/api/campaigns/", "campaigns");
    load("/api/broadcasts/", "broadcasts");
    load("/api/contacts/", "contacts");
  }, [getAccessTokenSilently]);

  const METRICS = [
    { key: "campaigns", title: "Signups", path: "/signups" },
    { key: "broadcasts", title: "Broadcasts", path: "/broadcasts" },
    { key: "contacts", title: "Contacts", path: "/contacts" },
  ];

  return (
    <Shell>
      <main>
        <Hero>
          <HeroInner>
            <div>
              <Eyebrow>Contacts · Outreach · Pipeline</Eyebrow>
              <HeroTitle>
                Every relationship, <em>anchored.</em>
              </HeroTitle>
              <HeroLede>
                Your steady home base for capturing leads, running outreach, and moving
                every deal to close.
              </HeroLede>
            </div>
            <CompassWrap>
              <CompassRose accent={compass.accent} line={compass.line} muted={compass.muted} />
            </CompassWrap>
          </HeroInner>
        </Hero>

        <CardGrid>
          <GettingStartedCard metrics={metrics} />
          <Row gutter={24} style={{ marginBottom: 8 }}>
            {METRICS.map((m) => (
              <Col key={m.key} xs={24} sm={8} style={{ marginBottom: 24 }}>
                <Card hoverable style={{ cursor: "pointer" }} onClick={() => navigate(m.path)}>
                  <Statistic title={m.title} value={metrics[m.key] == null ? "—" : metrics[m.key]} />
                </Card>
              </Col>
            ))}
          </Row>
          <Row gutter={24}>
            {QUICK_ACTIONS.map((action) => (
              <Col key={action.name} xs={24} sm={12} md={8} style={{ marginBottom: 24 }}>
                <Card hoverable style={{ height: "100%" }}>
                  <IconCircle>{action.icon}</IconCircle>
                  <Title heading={5} style={{ marginTop: 0 }}>
                    {action.name}
                  </Title>
                  <Paragraph style={{ color: "var(--color-text-2)" }}>
                    {action.description}
                  </Paragraph>
                  <Button type="primary" long onClick={() => navigate(action.path)}>
                    Open {action.name}
                  </Button>
                </Card>
              </Col>
            ))}
          </Row>
        </CardGrid>
      </main>

      <Footer>
        <Text style={{ color: "var(--color-text-3)" }}>
          {"Contact Anchor © "}
          {new Date().getFullYear()}
        </Text>
      </Footer>
    </Shell>
  );
}
